import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";
import config from "../config";

export const PORTAL_META = [
  { id: "public", label: "Public website", hint: "Home, about, catalogue, articles, support pages" },
  { id: "customer", label: "Customer portal", hint: "Login, booking, dashboard, profile" },
  { id: "nurse", label: "Nurse portal", hint: "/nurse visits and attendance" },
  { id: "doctor", label: "Doctor portal", hint: "/doctor reviews and prescriptions" },
  { id: "ops", label: "Ops portal", hint: "/ops board" },
  { id: "hospital", label: "Hospital portal", hint: "/hospital-dashboard" },
  { id: "admin", label: "Admin portal", hint: "Ops admin users. Super admin always stays up." },
];

export const DEFAULT_MAINTENANCE = {
  headline: "We're working on this page",
  message: "to bring good changes for you. We'll be back shortly.",
  portals: {
    public: false,
    customer: false,
    nurse: false,
    doctor: false,
    ops: false,
    hospital: false,
    admin: false,
  },
};

const CMS_KEY = "site_maintenance";

function pickPortals(raw) {
  const portals = { ...DEFAULT_MAINTENANCE.portals };
  if (!raw || typeof raw !== "object") return portals;
  Object.keys(portals).forEach((key) => {
    portals[key] = Boolean(raw[key]);
  });
  return portals;
}

export function parseMaintenancePayload(data) {
  if (!data || typeof data !== "object" || data.detail) return null;
  const nested = data.sections && !Array.isArray(data.sections) ? data.sections : null;
  const src = data.portals ? data : nested;
  if (!src || typeof src !== "object" || !src.portals || typeof src.portals !== "object") return null;
  return {
    headline: String(src.headline || data.headline || DEFAULT_MAINTENANCE.headline).trim() || DEFAULT_MAINTENANCE.headline,
    message: String(src.message || data.message || DEFAULT_MAINTENANCE.message).trim() || DEFAULT_MAINTENANCE.message,
    portals: pickPortals(src.portals),
    updated_at: data.updated_at || src.updated_at || "",
  };
}

const fetchOpts = () => ({
  headers: { "Cache-Control": "no-cache", Pragma: "no-cache" },
  params: { _: Date.now() },
});

export async function loadMaintenanceConfig() {
  try {
    const { data } = await axios.get(`${config.API}/config/maintenance`, fetchOpts());
    const parsed = parseMaintenancePayload(data);
    if (parsed) return parsed;
  } catch {
    /* dedicated endpoint is missing on older API builds */
  }
  const { data } = await axios.get(`${config.API}/cms/${CMS_KEY}`, fetchOpts());
  const parsed = parseMaintenancePayload(data);
  if (parsed) return parsed;
  throw new Error("Maintenance config is not available");
}

export async function saveMaintenanceConfig(payload, headers) {
  const body = {
    headline: payload.headline,
    message: payload.message,
    portals: pickPortals(payload.portals),
  };
  try {
    const { data } = await axios.put(`${config.API}/admin/config/maintenance`, body, { headers });
    const parsed = parseMaintenancePayload(data);
    if (parsed) return parsed;
    return { ...DEFAULT_MAINTENANCE, ...body };
  } catch (err) {
    const status = err.response?.status;
    if (status !== 404 && status !== 405) throw err;
    const { data } = await axios.put(
      `${config.API}/admin/cms/${CMS_KEY}`,
      {
        key: CMS_KEY,
        title: "Site maintenance",
        published: true,
        sections: body,
      },
      { headers },
    );
    return parseMaintenancePayload(data) || { ...DEFAULT_MAINTENANCE, ...body, updated_at: new Date().toISOString() };
  }
}

const MaintenanceContext = createContext(null);

export function portalFromPath(pathname = "") {
  const p = pathname || "";
  if (p.startsWith("/admin-login") || p.startsWith("/admin/login")) return null;
  if (p.startsWith("/admin")) return "admin";
  if (p.startsWith("/nurse")) return "nurse";
  if (p.startsWith("/doctor")) return "doctor";
  if (p.startsWith("/ops")) return "ops";
  if (p.startsWith("/hospital-dashboard")) return "hospital";
  if (
    p.startsWith("/login")
    || p.startsWith("/signup")
    || p.startsWith("/register")
    || p.startsWith("/onboarding")
    || p.startsWith("/dashboard")
    || p.startsWith("/booking")
    || p.startsWith("/profile")
  ) return "customer";
  return "public";
}

export function MaintenanceProvider({ children }) {
  const [cfg, setCfg] = useState(DEFAULT_MAINTENANCE);
  const [loaded, setLoaded] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const next = await loadMaintenanceConfig();
      setCfg(next);
    } catch {
      setCfg((prev) => (prev?.updated_at ? prev : DEFAULT_MAINTENANCE));
    } finally {
      setLoaded(true);
    }
  }, []);

  useEffect(() => {
    refresh();
    const timer = setInterval(refresh, 20000);
    return () => clearInterval(timer);
  }, [refresh]);

  const value = useMemo(() => ({ cfg, loaded, refresh }), [cfg, loaded, refresh]);
  return <MaintenanceContext.Provider value={value}>{children}</MaintenanceContext.Provider>;
}

export function useMaintenance() {
  const ctx = useContext(MaintenanceContext);
  if (!ctx) {
    return { cfg: DEFAULT_MAINTENANCE, loaded: true, refresh: async () => {} };
  }
  return ctx;
}

export function useCurrentPortalPaused() {
  const { cfg, loaded } = useMaintenance();
  if (typeof window === "undefined") return false;
  const portal = portalFromPath(window.location.pathname);
  if (!portal || !loaded) return false;
  return Boolean(cfg.portals?.[portal]);
}
